
import { v4 as uuidv4 } from "uuid";
import { 
  IStringNumOrStringHash 
} from "@models";
import { 
  CharacterClass, 
  Spell, 
  Attack,
  CharacterFeatureSource,
  AbilityEffect,
  CharacterSheetInterface,
  SpellOverrides,
  BasicCreatureInstance,
  CharacterAction,
} from "@dndModels";
import { AttackType, CastingTime, DamageType, EffectType, SpellLevel } from "@dndModels/Types";

export class CharacterSpell {
  data_type = "";
  true_id: string;
  _name = "";
  hidden = false;
  show_upcasts: number[] = [];
  hide_ritual = false;
  hide_in_pdf = false;
  show_upcasts_in_pdf: number[] = [];
  hide_ritual_in_pdf = false;
  spell_id = "";
  spell: Spell | null = null;
  customizations: IStringNumOrStringHash = {};
  // favorite_options: string[] = [];
  source_type = ""; // Class, Race, Feat, or Item
  source_id = "";
  source_name = "";
  source_true_id = "";
  always_known = false; // If this is true then it can't be unprepared, and it doesn't count against their spells prepared
  ritual = false;
  ritual_only = false;
  at_will = false;
  spellcasting_ability = "";
  spell_dc = 0;
  spell_attack = 0;
  spellcasting_modifier = 0;
  bonus_damage = 0;
  // This holds bonus damage from features
  attack: Attack = new Attack();
  // These two are for Spellbooks like Wizards have.
  // If they don't have a Spellbook 
  // then if it's not prepared/known then they just 
  // don't have a CharacterSpell for it.
  prepared: boolean | null = null;
  extra = false; // False if they learn at level up (free).  True if they transcribed it to get an extra spell.
  // attack_string: string = "";
  // damage_string: string = "";
  calc_range = "";
  modifications: SpellModification[] = [];
  booked_modifications: SpellModification[] = [];
  custom_modifications: SpellModification[] = [];
  spell_overrides: SpellOverrides[] = [];

  cf_id = "";
  ff_id = "";
  active = false;
  favorite = false;

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.spell_id = obj.spell_id ? obj.spell_id : "";
      this.spell = obj.spell ? new Spell(obj.spell) : null;
      this._name = obj._name ? obj._name : "";
      this.hidden = obj.hidden ? obj.hidden : false;
      this.show_upcasts = obj.show_upcasts ? obj.show_upcasts : [];
      this.hide_ritual = obj.hide_ritual ? obj.hide_ritual : false;
      this.hide_in_pdf = obj.hide_in_pdf ? obj.hide_in_pdf : false;
      this.show_upcasts_in_pdf = obj.show_upcasts_in_pdf ? obj.show_upcasts_in_pdf : [];
      this.hide_ritual_in_pdf = obj.hide_ritual_in_pdf ? obj.hide_ritual_in_pdf : false;
      this.customizations = obj.customizations ? obj.customizations : {};
      this.custom_modifications = obj.custom_modifications ? obj.custom_modifications.map((o: any) => new SpellModification(o)) : [];
      this.modifications = obj.modifications ? obj.modifications.map((o: any) => new SpellModification(o)) : [];
      this.booked_modifications = obj.booked_modifications ? obj.booked_modifications.map((o: any) => new SpellModification(o)) : [];
      // this.favorite_options = obj.favorite_options ? obj.favorite_options : [];
      this.source_type = obj.source_type ? obj.source_type : "";
      this.source_id = obj.source_id ? obj.source_id : "";
      this.source_name = obj.source_name ? obj.source_name : "";
      this.always_known = obj.always_known ? obj.always_known : false;
      this.ritual = obj.ritual ? obj.ritual : false;
      this.ritual_only = this.ritual && obj.ritual_only ? obj.ritual_only : false;
      this.at_will = obj.at_will ? obj.at_will : false;
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : "";
      this.spell_attack = obj.spell_attack ? +obj.spell_attack : 0;
      this.attack = obj.attack ? obj.attack : new Attack();
      this.bonus_damage = obj.bonus_damage ? +obj.bonus_damage : 0;
      this.spell_dc = obj.spell_dc ? +obj.spell_dc : 0;
      this.spellcasting_modifier = obj.spellcasting_modifier ? obj.spellcasting_modifier : 0;
      this.prepared = obj.prepared ? obj.prepared : null;
      this.extra = obj.extra ? obj.extra : false;
      this.cf_id = obj.cf_id ? obj.cf_id : "";
      this.ff_id = obj.ff_id ? obj.ff_id : "";
      this.active = obj.active ? obj.active : false;
      this.favorite = obj.favorite ? obj.favorite : false;
    }
    // this.attack_string = "";
    // this.damage_string = "";
    // this.recalc_attack_string(null);
  }

  get the_spell(): Spell | null {
    return this.spell;
  }

  get spell_list_ids() {
    return this.spell?.spell_list_ids??[];
  }

  get use_spellcasting_modifier() {
    const use_mod_overrides = this.use_spell_overrides.find(o => o.include_modifier_on_damage === "True");
    return use_mod_overrides !== undefined;
  }

  get damage_die_size_override() {
    const damage_die_size_override = this.use_spell_overrides.find(o => o.damage_die_size_override !== -1);
    return damage_die_size_override?.damage_die_size_override??-1;
  }

  get name(): string {
    let name = this._name;
    if (this.spell) {
      if (name === "") {
        name = this.spell.name;
      } else if (!name.includes(this.spell.name)) {
        name = this.spell.name + " - " + name;
      }
    }
    return name;
  }

  get true_name(): string {
    let name = this._name;
    if (this.spell) {
      if (name === "") {
        name = this.spell.true_name;
      } else if (!name.includes(this.spell.true_name)) {
        name = this.spell.true_name + " - " + name;
      }
    }
    return name;
  }

  get description(): string {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.description;
    }
    return "";
  }

  set name(value: string) {
    this._name = value;
  }

  get source_book_ids() {
    if (this.the_spell) {
      return this.the_spell.source_book_ids;
    } else {
      return [];
    }
  }

  get level() {
    if (this.the_spell) {
      return this.the_spell.level;
    } else {
      return 0;
    }
  }

  get target_types() {
    if (this.the_spell) {
      return this.the_spell.target_types;
    } else {
      return [];
    }
  }

  get world_settings() {
    if (this.the_spell) {
      return this.the_spell.world_settings;
    } else {
      return [];
    }
  }

  get base_slot_level() {
    if (this.the_spell) {
      return this.the_spell.level;
    } else {
      return 0;
    }
  }

  get school(): string {
    const the_spell = this.the_spell;
    if (the_spell && the_spell.school) {
      return the_spell.school;
    }
    return "";
  }

  get duration() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.duration;
    }
    return "";
  }

  get range_string() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.range_string;
    }
    return "";
  }

  get can_be_active(): boolean {
    return !this.concentration && this.duration !== "Instantaneous";
  }

  get level_and_school(): string {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.level_and_school;
    }
    return "";
  }

  get level_string(): string {
    const level = this.level;
    let level_str = "";
    if (level === 0) {
      level_str = "Cantrip";
    } else if (level === 1) {
      level_str = "1st";
    } else if (level === 2) {
      level_str = "2nd";
    } else if (level === 3) {
      level_str = "3rd";
    } else {
      level_str = `${level}th`;
    }
    return level_str;
  }

  get effects(): AbilityEffect[] {
    if (this.the_spell) {
      return this.the_spell.effects;
    }
    return [];
  }

  get effect_types(): EffectType[] {
    return this.effects.map(o => o.type);
  }

  get attack_types(): AttackType[] {
    return this.effects.map(effect => { return effect.attack_type; });
  }

  get damage_types(): DamageType[] {
    const dt_mods = this.damage_type_modifications;
    if (dt_mods.length > 0) {
      return dt_mods.map(o => o.detail) as DamageType[];
    } else {
      return this.base_damage_types;
    }
  }

  get base_damage_types(): DamageType[] {
    return this.effects.flatMap(effect => { return effect.damage_types; });
  }

  get main_damage_type() {
    const damage_type = this.base_damage_types[0];
    if (damage_type) {
      return damage_type;
    }
    return "Typeless";
  }

  get casting_times(): CastingTime[] {
    const casting_time_overrides = this.use_spell_overrides.filter(o => o.casting_time_override !== "Don't Override");
    if (casting_time_overrides.length === 0) {
      if (this.spell) {
        return [this.spell.casting_time];
      }
    } else {
      const options: CastingTime[] = [];
      for (const override of casting_time_overrides) {
        if (override.casting_time_override === "Options") {
          for (const opt of override.casting_time_options) {
            if (opt === "Don't Override") {
              if (this.spell && !options.includes(this.spell.casting_time)) {
                options.push(this.spell.casting_time);
              }
            } else if (!options.includes(opt)) {
              options.push(opt);
            }
          }
        } else if (override.casting_time_override === "Don't Override") {
          // Will never come here.  It's just to make the TS happy for the next one.
        } else if (!options.includes(override.casting_time_override)) {
          options.push(override.casting_time_override);
        }
      }
      return options;
    }
    return ["A"];
  }

  // get casting_time_string(): string {
  //   let the_spell = this.the_spell;
  //   if (the_spell) {
  //     const casting_time = the_spell.casting_time;
  //     if (casting_time === "A") {
  //       return "Action";
  //     } else if (casting_time === "BA") {
  //       return "Bonus Action";
  //     } else if (casting_time === "RA") {
  //       return "Reaction";
  //     } else if (casting_time === "Special") {
  //       return the_spell.special_casting_time === "" ? "Special" : the_spell.special_casting_time;
  //     } else {
  //       return casting_time;
  //     }
  //   }
  //   return "";
  // }

  get variant_of_id(): string {
    if (this.the_spell) {
      return this.the_spell.variant_of_id;
    }
    return "";
  }

  get variant_group_id() {
    return this.the_spell?.variant_group_id??"";
  }

  get variant_group() {
    return this.the_spell?.variant_group??[];
  }

  get _id() {
    return this.the_spell?._id??"";
  }

  get id() {
    return this.true_id;
  }

  get use_name() {
    return this.the_spell?.use_name??"";
  }

  get concentration_modifications(): SpellModification[] {
    return this.all_modifications.filter(o => o.mod_type === "Concentration");
  }

  get concentration(): boolean {
    if (this.the_spell) {
      return this.the_spell.concentration;
    } 
    return false;
  }

  get knowledge_usage_key(): string {
    if (this.spell) {
      return this.spell.knowledge_usage_key;
    }
    return "";
  }

  get knowledge_type() {
    if (this.spell) {
      return this.spell.knowledge_type;
    }
    return "None";
  }

  get obj_filter() {
    if (this.spell) {
      return this.spell.obj_filter;
    }
    return {};
  }

  get casting_time() {
    if (this.spell) {
      return this.spell.casting_time;
    }
    return "A";
  }

  get special_casting_time() {
    if (this.spell) {
      return this.spell.special_casting_time;
    }
    return "";
  }

  get contaminated() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.contaminated;
    }
    return false;
  }

  get upcasts() {
    return this.the_spell?.upcasts??false;
  }

  get charm() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.charm;
    }
    return false;
  }

  get disease() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.disease;
    }
    return false;
  }

  use_string(character: CharacterSheetInterface) {
    return "Cast";
  }

  disabled({ 
    char, 
    action, 
    level = -1, 
    type = "" 
  }: { 
    char: CharacterSheetInterface | BasicCreatureInstance, 
    action?: CharacterAction, 
    level?: number, 
    type?: string 
  }) {
    if (this.spell && !(char instanceof BasicCreatureInstance)) {
      const filtered_slots = char.body.use_slots.filter(o => 
        o.level === level && o.used < o.total
      );
      return filtered_slots.length === 0;
    }
    return true;
  }

  // disabled(character: CharacterSheetInterface, level: number = -1) {
  //   if (this.spell) {
  //     const filtered_slots = character.body.use_slots.filter(o => 
  //       o.level === level && o.used < o.total
  //     );
  //     return filtered_slots.length === 0;
  //   }
  //   return true;
  // }

  get component_modifications(): SpellModification[] {
    return this.all_modifications.filter(o => o.mod_type === "Components");
  }

  get use_spell_overrides() {
    return this.spell_overrides;
  }

  get components(): string[] {
    if (this.the_spell) {
      let components = this.the_spell.components;
      const component_overrides = this.use_spell_overrides.filter(o => !o.components_override.includes("Don't Override"));
      for (const override of component_overrides) {
        if (override.components_override.length === 0) {
          components = [];
        } else {
          if (override.components_override.includes("No V")) {
            components = components.filter(o => o !== "V");
          }
          if (override.components_override.includes("No S")) {
            components = components.filter(o => o !== "S");
          }
          if (override.components_override.includes("No M")) {
            components = components.filter(o => o !== "M");
          }
        }
        if (components.length === 0) {
          break;
        }
      }
      const removed_components = this.component_modifications.map(o => o.detail);
      
      return components.filter(o => !removed_components.includes(o));
    }
    return [];
  }

  get material_component(): string {
    if (this.the_spell) {
      return this.the_spell.material_component;
    }
    return "";
  }

  get material_consumed(): boolean {
    if (this.the_spell) {
      return this.the_spell.material_consumed;
    }
    return false;
  }

  get material_cost_listed(): boolean {
    if (this.the_spell) {
      return this.the_spell.material_cost_listed;
    }
    return false;
  }

  get components_string(): string {
    let components_string = "";
    const components = this.components;
    if (components.includes("V")) {
      components_string = "V";
    }
    if (components.includes("S")) {
      if (components_string.length > 0) {
        components_string += ", ";
      }
      components_string += "S";
    }
    if (components.includes("M")) {
      if (components_string.length > 0) {
        components_string += ", ";
      }
      components_string += "M";
      components_string += ` (${this.material_component})`;
    }
    return components_string;
  }

  get sight() {
    if (this.the_spell) {
      return this.the_spell.sight;
    }
    return false;
  }

  get duration_string(): string {
    let duration_string = "";
    if (this.the_spell) {
      duration_string = this.the_spell.duration;
    }
    return duration_string;
  }

  get damage_type_modifications(): SpellModification[] {
    return this.all_modifications.filter(o => o.mod_type === "Damage Type");
  }

  get all_modifications(): SpellModification[] {
    return [...this.modifications,...this.custom_modifications,...this.booked_modifications];
  }

  get ritual_modifications(): SpellModification[] {
    return this.all_modifications.filter(o => o.mod_type === "Ritual");
  }

  get targets_modifications(): SpellModification[] {
    return this.all_modifications.filter(o => o.mod_type === "Targets");
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      spell_id: this.spell_id,
      cf_id: this.cf_id,
      ff_id: this.ff_id,
      modifications: this.modifications.map(o => o.toDBObj()),
      custom_modifications: this.custom_modifications.map(o => o.toDBObj()),
      booked_modifications: this.booked_modifications.map(o => o.toDBObj()),
      _name: this._name,
      hidden: this.hidden,
      show_upcasts: this.show_upcasts,
      hide_ritual: this.hide_ritual,
      hide_in_pdf: this.hide_in_pdf,
      show_upcasts_in_pdf: this.show_upcasts_in_pdf,
      hide_ritual_in_pdf: this.hide_ritual_in_pdf,
      customizations: this.customizations,
      // favorite_options: this.favorite_options,
      source_type: this.source_type,
      source_id: this.source_id,
      source_name: this.source_name,
      always_known: this.always_known,
      prepared: this.prepared,
      extra: this.extra,
      active: this.active,
      favorite: this.favorite,
    };
  }

  copySpell(copyMe: Spell) {
    this.spell_id = copyMe._id;
    this.spell = copyMe;
  }

  connectSpell(spell: Spell) {
    this.spell = spell;
  }

  connectSource(source: CharacterFeatureSource) {
    if (source instanceof CharacterClass) {
      this.source_type = "Class";
      this.source_id = source.source_id;
      this.source_name = source.name;
      this.source_true_id = source.true_id;
      this.spellcasting_ability = source.spellcasting_ability;
      // if (this.name.includes("Eldritch")) {
      //   debugger;
      // }
      this.spell_attack = +source.spell_attack;
      this.spell_dc = +source.spell_dc;
      this.spellcasting_modifier = source.spellcasting_modifier;
      if (source.source) {
        if (this.spell && this.spell.ritual && source.ritual_casting) {
          this.ritual = true;
        }
      }
    } else {
      this.source_type = source.source_type;
      this.source_id = source.source_id;
      this.source_name = source.name;
      this.source_true_id = source.true_id;
    }
  }

  clone(drop_ids = false): CharacterSpell {
    const new_obj = new CharacterSpell();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: CharacterSpell, drop_ids = false) {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.spell_id = copyMe.spell_id;
    this.cf_id = copyMe.cf_id;
    this.ff_id = copyMe.ff_id;
    this.modifications = copyMe.modifications.map(o => o.clone());
    this.custom_modifications = copyMe.custom_modifications.map(o => o.clone());
    this.booked_modifications = copyMe.booked_modifications.map(o => o.clone());
    this.spell = copyMe.spell?.clone() ?? null;
    this._name = copyMe._name;
    this.hidden = copyMe.hidden;
    this.show_upcasts = [...copyMe.show_upcasts];
    this.hide_ritual = copyMe.hide_ritual;
    this.hide_in_pdf = copyMe.hide_in_pdf;
    this.show_upcasts_in_pdf = [...copyMe.show_upcasts_in_pdf];
    this.hide_ritual_in_pdf = copyMe.hide_ritual_in_pdf;
    this.customizations = {...copyMe.customizations};
    // this.favorite_options = [...copyMe.favorite_options];
    this.source_type = copyMe.source_type;
    this.source_id = copyMe.source_id;
    this.source_name = copyMe.source_name;
    this.source_true_id = copyMe.source_true_id;
    this.always_known = copyMe.always_known;
    this.ritual = copyMe.ritual;
    this.ritual_only = copyMe.ritual_only;
    this.at_will = copyMe.at_will;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spell_attack = copyMe.spell_attack;
    this.spellcasting_modifier = copyMe.spellcasting_modifier;
    this.attack = copyMe.attack;
    this.bonus_damage = copyMe.bonus_damage;
    this.spell_dc = copyMe.spell_dc;
    this.prepared = copyMe.prepared;
    this.extra = copyMe.extra;
    this.active = copyMe.active;
    this.favorite = copyMe.favorite;
  }
}
/**
    Components. Remove	one	of	the	spell’s	
  components:	Verbal,	Somatic,	or	Material. You	
  can’t	remove	the	Material	component	of	a	spell	
  that	consumes	that	component.
  Concentration.	If	the	spell	requires	
  Concentration,	damage	can’t	break	your	
  Concentration	on	the	spell.
  Damage	Type. If	the	spell	has	a	damage	type,	
  replace	it	with	one	of	the	following	types:	Acid,	
  Cold,	Fire,	Lightning,	Necrotic,	Poison,	or	
  Thunder.	If	the	spell	has	multiple	damage	
  types,	you	can	change	only	one	of	them.
  Range. If	the	spell	has	a	range	of	at	least	5	feet	
  and	doesn’t	have	a	range	of	Self,	increase	its	
  range	by	a	number	of	feet	equal	to	30	times	
  your	Wizard	level.
  Ritual. If	the	spell	lacks	the	Ritual	tag and	has	a	
  casting	time	of	at	least	10	minutes,	give	it	the	
  Ritual	tag.
  Targets. If	the	spell	affects	one	or	more	
  creatures	and	doesn’t	have	a	range	of	Self,	it	
  now	affects	only	your	allies	or	enemies	
  (choose	which	when	you	cast	Modify	Spell).
  */
export class SpellModification {
  true_id = "";
  mod_type: "Components" | "Concentration" | "Damage Type" | "Range" | "Ritual" | "Targets" | "None" = "None";
  detail = "";

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.mod_type = obj.mod_type ? obj.mod_type : "None";
      this.detail = obj.detail ? obj.detail : "";
    }
  }

  toDBObj() {
    return {
      true_id: this.true_id,
      mod_type: this.mod_type,
      detail: this.detail
    }
  }

  copy(copyMe: SpellModification, drop_ids = false) {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.mod_type = copyMe.mod_type;
    this.detail = copyMe.detail;
  }

  clone() {
    const new_obj = new SpellModification();
    new_obj.copy(this, true);
    return new_obj;
  }
}

export class StoredSpell {
  true_id = "";
  item_id = "";
  spell_id = "";
  spell_dc = 0;
  spell_attack = 0;
  spell_mod = 0;
  level: SpellLevel = 0;
  favorite = false;
  hidden = false;
  hide_in_pdf = false;
  attack: Attack = new Attack();
  castings = 1;
  has_been_cast = 0;
  active = false;

  the_spell: Spell | undefined = undefined;
  
  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.item_id = obj.item_id ? obj.item_id : "";
      this.spell_id = obj.spell_id ? obj.spell_id : "";
      this.spell_dc = obj.spell_dc ? obj.spell_dc : 0;
      this.spell_attack = obj.spell_attack ? obj.spell_attack : 0;
      this.spell_mod = obj.spell_mod ? obj.spell_mod : 0;
      this.level = obj.spell_level ? obj.spell_level : 0;
      this.favorite = obj.favorite ? obj.favorite : false;
      this.hidden = obj.hidden ? obj.hidden : false;
      this.hide_in_pdf = obj.hide_in_pdf ? obj.hide_in_pdf : false;
      this.castings = obj.castings !== undefined ? obj.castings : 1;
      this.has_been_cast = obj.has_been_cast ? (typeof obj.has_been_cast === "boolean" ? 1 : obj.has_been_cast) : 0;
      this.active = obj.active ? obj.active : false;
    }
  }

  get true_name() {
    if (this.the_spell) {
      return this.the_spell.true_name;
    }
    return "";
  }

  get spell() {
    return this.the_spell;
  }

  get id() {
    return this.true_id;
  }

  get name() {
    return this.true_name;
  }

  get description() {
    if (this.the_spell) {
      return this.the_spell.description;
    }
    return "";
  }

  get effects() {
    if (this.the_spell) {
      return this.the_spell.effects;
    }
    return [];
  }

  get source_true_id() {
    return this.item_id;
  }

  get can_be_active() {
    return !this.concentration && this.duration !== "Instantaneous";
  }

  get concentration() {
    return this.the_spell?.concentration??false;
  }

  get sight() {
    return this.the_spell?.sight??false;
  }

  get at_will() {
    return false;
  }

  get source_id() {
    return "";
  }

  get damage_types() {
    return this.the_spell?.damage_types??[];
  }

  get effect_types() {
    return this.the_spell?.effect_types??[];
  }

  get casting_times() {
    return this.the_spell?.casting_times??[];
  }

  get duration() {
    return this.the_spell?.duration??undefined;
  }

  get range_string() {
    const the_spell = this.the_spell;
    if (the_spell) {
      return the_spell.range_string;
    }
    return "";
  }

  get components() {
    return this.the_spell?.components??[];
  }

  get material_component() {
    return this.the_spell?.material_component??undefined;
  }

  get components_string(): string {
    let components_string = "";
    const components = this.components;
    if (components.includes("V")) {
      components_string = "V";
    }
    if (components.includes("S")) {
      if (components_string.length > 0) {
        components_string += ", ";
      }
      components_string += "S";
    }
    if (components.includes("M")) {
      if (components_string.length > 0) {
        components_string += ", ";
      }
      components_string += "M";
      components_string += ` (${this.material_component})`;
    }
    return components_string;
  }

  get level_and_school() {
    const the_spell = this.the_spell;
    if (the_spell) {
      if (the_spell.level === 0) {
        return `${the_spell.school} Cantrip`;
      } else {
        return `${this.level_string} Level ${the_spell.school}`;
      }
    }
    return "";
  }

  get level_string(): string {
    const level = this.level;
    let level_str = "";
    if (level === 0) {
      level_str = "Cantrip";
    } else if (level === 1) {
      level_str = "1st";
    } else if (level === 2) {
      level_str = "2nd";
    } else if (level === 3) {
      level_str = "3rd";
    } else {
      level_str = `${level}th`;
    }
    return level_str;
  }

  toDBObj() {
    return {
      true_id: this.true_id,
      item_id: this.item_id,
      spell_id: this.spell_id,
      spell_dc: this.spell_dc,
      spell_attack: this.spell_attack,
      spell_mod: this.spell_mod,
      spell_level: this.level,
      favorite: this.favorite,
      hidden: this.hidden,
      hide_in_pdf: this.hide_in_pdf,
      castings: this.castings,
      has_been_cast: this.has_been_cast,
      active: this.active,
    }
  }

  copy(copyMe: StoredSpell) {
    this.true_id = copyMe.true_id;
    this.item_id = copyMe.item_id;
    this.spell_id = copyMe.spell_id;
    this.spell_dc = copyMe.spell_dc;
    this.spell_attack = copyMe.spell_attack;
    this.spell_mod = copyMe.spell_mod;
    this.level = copyMe.level;
    this.favorite = copyMe.favorite;
    this.hidden = copyMe.hidden;
    this.hide_in_pdf = copyMe.hide_in_pdf;
    this.attack = copyMe.attack.clone();
    this.castings = copyMe.castings;
    this.has_been_cast = copyMe.has_been_cast;
    this.active = copyMe.active;
  }

  clone() {
    const new_obj = new StoredSpell();
    new_obj.copy(this);
    return new_obj;
  }
}